import {Box3, BufferGeometry, Group, Mesh, Object3D, Vector3} from 'three';

import {Object3DFactoryService} from '../service/object3D.factory.service';
import {LamellaSetObject} from "./lamellaSetObject";
import {Generic3DModel} from "../models/generic-3D-model";
import {Movable} from "../models/feature/movable.feature";
import {Features} from "../decorators/features.decorator";
import {Scalable} from "../models/feature/scalable.feature";
import {Extendable} from "../models/feature/extendable.feature";
import {Insertable} from "../models/feature/insertable.feature";
import {FrameProfileObject} from "./frameProfileObject";

import {ColorInfo} from "../../core/util/color-converter";
import {MountingProfileObject} from "./mountingProfileObject";
import {MountingProfile} from "../../shared/product-card/product-card/profiles/mounting-profile/mounting-profile";
import {CustomMaterialType} from "../service/material/custom-material-type.enum";
import {dispose3dResource} from "../util/dispose-object";
import {GeneralProfile} from "../../shared/product-card/product-card/profiles/general-profile/general-profile";
import {isDefined} from "../../core/util/isDefined";
import {LamellaSet} from "../../shared/product-card/product-card/lamel/lamella-set.class";


type CreationData = {
    lamellaSetData: LamellaSet;
    positionY: number;             // m
};


@Features(Movable, Scalable, Extendable, Insertable)
export class LamellaAreaObject extends Generic3DModel<Group> {
    private lamellaSetObjects: LamellaSetObject[] = [];
    // Muutetaan tyyppi yleisemmäksi, jotta myös InstancedMesh hyväksytään.
    private mountingProfileObjects: MountingProfileObject[] = [];
    private frameProfileObjects: FrameProfileObject[] = [];

    private scaleFactor: number = 10; // Koko objektia on pakko skaalata jotta valon taitos on hieno.
    private assemblyProfileSlotDepth: number = 0.0455; // Slotin syvyys kammassa.

    private readonly lamellaSetObjectContainer: Group = new Group();

    constructor(
        public data: any,
        private object3DFactoryService: Object3DFactoryService,
    ) {
        super(
            'LamellaAreaObject',
            new Group()
        );
        this.data = {
            ...data,
            width: this.data.orientation === "horizontal" ? data.width : data.height,
            height: this.data.orientation === "horizontal" ? data.height : data.width,
        };

        // Lisätään container pääobjektiin
        this._object.add(this.lamellaSetObjectContainer);
    }

    public async init(): Promise<void> {
        this._object.visible = false;
        return Promise.allSettled([
            //this.insertInstallationProfiles(this.installationProfilePositions(this.data.orientation)),
            this.insertLamellaSets(this.data.lamellaSets),
            this.insertMountingProfiles(this.installationProfilePositions(this.data.orientation)),
            isDefined(this.data.frameProfile)
                ? this.insertFrameProfiles(this.installationProfilePositions(this.data.orientation), this.data.frameProfile)
                : Promise.resolve()

        ]).then(() => {
            this.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);

            if (this.data.orientation === 'vertical') {
                this._object.rotateZ(-Math.PI / 2);

            }
            this._object.visible = true;
            this._object.updateMatrixWorld(true);
        });
    }

    private async insertMountingProfiles(positions: Vector3[]) {
        const profileDepth = 0.022; // Profiilin syvyys
        const profileHalfWidth = 0.0205; // Profiilin puolet leveys
        // Luodaan lupauksia jokaiselle mountingProfileObjectin luomiselle
        const mountingProfilePromises = positions.map(async (position, index) => {
            // Kopioidaan alkuperäinen sijainti, jotta emme muuta sitä
            const correctedPosition = { ...position };
            correctedPosition.z = profileDepth - this.assemblyProfileSlotDepth;

            //Kauneus korjaus jos tolppa on päädyssä
            if(index === 0 && correctedPosition.x < profileHalfWidth){
                correctedPosition.x = profileHalfWidth;
            }

            //Kauneus korjaus jos tolppa on on viimeinen
            if(correctedPosition.x > (this.data.width/1000) - profileHalfWidth){
                correctedPosition.x = (this.data.width/1000) - profileHalfWidth
            }

            // Luodaan mountingProfileObject ja asetetaan sen sijainti
            return this.object3DFactoryService.createMountingProfileObject({...this.data.mountingProfile, length: this.data.height})
                .then((mountingProfileObject)=> {
                    mountingProfileObject.position.copy(correctedPosition);
                    return mountingProfileObject;
                });
        });

        try {
            // Odotetaan, että kaikki lupaukset valmistuvat samanaikaisesti
            return Promise.all(mountingProfilePromises)
                .then((mountingProfileObjects)=>{
                    mountingProfileObjects.forEach(obj => {
                        this.mountingProfileObjects.push(obj);
                        this.extend(obj.object());
                    });
                });
        } catch (error) {
            console.error("Virhe mounting profile -objektien luomisessa:", error);
        }
    }

    public async setOrientation(orientation: 'horizontal' | 'vertical'): Promise<void> {

        // Päivitetään dataan tallennettu orientaatio
        this.data.orientation = orientation;

        return Promise.allSettled([
            //this.rebuildInstallationProfiles(),
            this.rebuildSets(this.data.lamellaSets)
        ]).then(()=>{

            //update rotation of the object
            orientation === 'horizontal'
                ? this._object.rotateZ(-Math.PI / 2)
                : this._object.rotateZ(Math.PI / 2);

            this._object.position.set(0,0,0);

        });

    }

    public removeFrameProfiles(): void {
        this.frameProfileObjects.forEach((frameProfileObject) => frameProfileObject.destroy());
        this.frameProfileObjects = [];
    }

    public removeMountingProfiles(): void {
        this.mountingProfileObjects.forEach((mountingProfileObject: MountingProfileObject) => mountingProfileObject.destroy());
        this.mountingProfileObjects = [];
    }

    public async rebuildFrameProfiles(profile: GeneralProfile, positions : any): Promise<void> {
        this.data.positions = positions;
        this.data.frameProfile = profile;
        this.removeFrameProfiles();
        return this.insertFrameProfiles(this.installationProfilePositions(this.data.orientation), profile)
    }

    public async rebuildMountingProfiles(mountingProfile: GeneralProfile, positions: any = this.data.positions): Promise<void> {
        this.data.positions = positions;
        this.data.mountingProfile = mountingProfile;
        //No need to handle mountingProfile, we only got one option
        this.removeMountingProfiles();
        return this.insertMountingProfiles(this.installationProfilePositions(this.data.orientation));
    }

    /**
     * Varmistaa, että objektin (ja sen lapsien) geometria sekä
     * matriisit on päivitetty ennen bounding boxin laskentaa.
     * Palauttaa lasketun Box3-olion.
     */
    private computeAccurateBoundingBox(): Box3 {
        // Käy rekursiivisesti läpi kaikki lapset ja
        // päivitä tarvittaessa geometryn bounding box.
        this._object.traverse((child: Object3D) => {
            if (child instanceof Mesh && child.geometry instanceof BufferGeometry) {
                child.geometry.computeBoundingBox();
            }
        });

        // Päivitä myös objektihierarkian maailmankoordinaatit
        // (position, scale, rotation).
        this._object.updateMatrixWorld(true);

        // Tee varsinainen bounding boxin laskenta.
        const box: Box3 = new Box3().setFromObject(this._object);
        return box;
    }

    // Keskittää objektin siten, että bounding boxin keskipiste osuu (0,0,0)
    private centerObject() {
        const box = this.computeAccurateBoundingBox();
        const center = box.getCenter(new Vector3());
        this._object.position.sub(center);
        //this.showBoundingBox();
    }

    public async insertLamellaSets(rawSets: LamellaSet[]): Promise<void> {
        const areaHeightM = this.data.height / 1000; // m
        const creationData: CreationData[] = [];

        let usedHeightM = 0;           // m
        let lastLamellaPosition = 0;   // m
        let previousSet: LamellaSet | null = null;

        /* Silmukka jatkuu, kunnes tila loppuu*/
        outer: while (usedHeightM < areaHeightM) {
            for (const current of rawSets) {
                const productChanged = previousSet && previousSet.productCode !== current.productCode;

                /* 1) spaceAfter tuotetyypin vaihtuessa (ENNEN settiä) */
                if (productChanged) {
                    usedHeightM = this.addGapIfNeeded(
                        usedHeightM,
                        lastLamellaPosition,
                        current.spaceAfter,
                    );
                }

                /* 2) Lasketaan setin toistojen määrä */
                const unitHeightM = this.unitHeight(current);
                const repeats = this.calculateRepeats(
                    usedHeightM,
                    areaHeightM,
                    unitHeightM,
                    current.lamellaRepeat,
                    current.lamellaSpaceBetween
                );
                if (repeats === 0) break outer; // tila loppui

                /* 3) Tallennetaan data ja päivitetään korkeus */
                const setCopy: LamellaSet = { ...current, lamellaRepeat: repeats };
                creationData.push({ lamellaSetData: setCopy, positionY: usedHeightM });

                usedHeightM += unitHeightM * repeats;
                lastLamellaPosition =
                    usedHeightM - current.lamellaSpaceBetween / 1000; // viimeisen säleen yläpinta

                /* 4) spaceBefore tuotetyypin vaihtuessa (SETIN JÄLKEEN) */
                if (productChanged) {
                    usedHeightM = this.addGapIfNeeded(
                        usedHeightM,
                        lastLamellaPosition,
                        current.spaceBefore,
                    );
                }

                previousSet = setCopy;
                if (usedHeightM >= areaHeightM) break outer;
            }
        }

        /* 5) Luodaan varsinaiset objektit (toteuta omien tarpeiden mukaan) */
        await this.createLamellaObjects(creationData);
    }

    private unitHeight(set: LamellaSet): number {
        // yhden toiston korkeus metreinä
        return (set.lamellaSpace + set.lamellaSpaceBetween) / 1000;
    }

    /**
     * Laskee suurimman toistomäärän, joka mahtuu vapaaseen tilaan.
     * Viimeisen rivin yläpuolelle ei varata spaceBetween-väliä.
     *
     * @param used   Korkeus, joka on jo käytössä (m)
     * @param max    Alueen kokonaiskorkeus (m)
     * @param unit   lamellaSpace + spaceBetween (m)
     * @param wanted Haluttu toistojen määrä tai Infinity
     * @param spaceBetween spaceBetween millimetreinä (mm)
     * @return toistojen määrä välillä 0…wanted
     */
    private calculateRepeats(
        used: number,
        max: number,
        unit: number,
        wanted: number | typeof Infinity,
        spaceBetween: number,
    ): number {
        const epsilon = 1e-6;

        // Vapaaksi jäävä korkeus (m)
        const free = max - used;

        /* -----------------------------------------------------------
         * Kokonaiskorkeus, jonka n toistoa vie:
         *   total = n * lamellaSpace + (n – 1) * spaceBetween
         * Ratkaistaan n:  n ≤ (free + spaceBetween) / (lamellaSpace + spaceBetween)
         * Koska unit = lamellaSpace + spaceBetween, riittää:
         *   n ≤ (free + spaceBetween) / unit
         * ---------------------------------------------------------- */

        // spaceBetween mukaan “alennuksena” (muunna metreiksi)
        const freeWithDiscount = free + spaceBetween / 1000 + epsilon;

        // Suurin kokonaisluku, joka mahtuu
        const possible = Math.floor(freeWithDiscount / unit);

        // Rajaa haluttuun määrään, ellei Infinity
        return wanted === Infinity ? possible : Math.min(wanted, possible);
    }

    /**
     * Lisää tarvittavan välyn, jos nykyinen välys (used - lastPos)
     * on pienempi kuin gapMm / 1000.
     */
    private addGapIfNeeded(
        used: number,       // m
        lastPos: number,    // m
        gapMm: number,      // mm
    ): number {
        if (gapMm <= 0) return used;
        const gapM = gapMm / 1000;
        const actualGap = used - lastPos;
        return actualGap >= gapM ? used : used + (gapM - actualGap);
    }

    private async createLamellaObjects(
        creationData: CreationData[],
    ): Promise<void> {
        // Luodaan lamellaSet-objektit rinnakkain esilaskettujen tietojen perusteella
        return Promise.all(
            creationData.map(async ({ lamellaSetData, positionY }) => {
                return this.object3DFactoryService
                    .createLamellaSetObject(lamellaSetData)
                    .then((lamellaSetObject) => {
                        // Asetetaan pituus (leveys) metreinä
                        lamellaSetObject.setLength(this.data.width / 1000);

                        // Sijoitetaan lamellaSet paikallisiin koordinaatteihin
                        lamellaSetObject.position.set(0, positionY, 0);

                        // Lisätään kontaineriin
                        this.lamellaSetObjectContainer.add(lamellaSetObject);

                        return lamellaSetObject;
                    });
            }),
        ).then((lamellaSetObjects) => {
            // Päivitetään globaali lista luoduista lamellaSet-objekteista
            this.lamellaSetObjects.push(...lamellaSetObjects);

            // Päivitetään kontainerin sijainti
            this.lamellaSetObjectContainer.position.copy(
                this.computeLamellaSetContainerPosition(),
            );
        });
    }

    // Poistaa asennusprofiilit ja lamella-setit sekä vapauttaa niihin liittyvät resurssit.
    public override destroy(): void {

        this.removeLamellaSets();

        this.removeMountingProfiles();

        this.removeFrameProfiles();

        dispose3dResource(this._object);

        delete this._object;

        this.destroyed.next();
    }

    public setLamellaSetColor(lamellaSetId: string, color: any) {
        this.lamellaSetObjects.forEach((lamellaSetObject: LamellaSetObject) => {
            if (lamellaSetObject.data.id === lamellaSetId) {
                lamellaSetObject.data.color = color
                lamellaSetObject.setColor(color.HEX);
            }
        });
    }


    public setLamellaSetMaterialAndColor(
        lamellaSetId: string,
        materialType: 'Powder Coated' | 'Anodized' | 'Galvanized Steel',
        color: ColorInfo
    ): Promise<void> {
        const ops: Promise<void>[] = [];

        this.lamellaSetObjects.forEach(ls => {
            if (ls.data.id === lamellaSetId) {
                ops.push(ls.setMaterial(materialType, color.HEX));
            }
        });

        if (ops.length === 0) {
            console.warn(`LamellaSet ${lamellaSetId} not found in area.`);
            return Promise.resolve();          // ei mitään päivitettävää
        }

        // Odota kaikki päivityspromiset rinnakkain
        return Promise.all(ops).then(()=>this._changed.next());
    }

    public async setLamellaSetMaterialAndColorAsync(lamellaSetId: string, materialType: string, color: ColorInfo) {
        this.lamellaSetObjects.forEach((lamellaSetObject: LamellaSetObject) => {
            if (lamellaSetObject.data.id === lamellaSetId) {
                lamellaSetObject.setMaterial(materialType, color.HEX);
            }
        });
    }

    public removeLamellaSets(){
        this.lamellaSetObjects.forEach(lamellaSet => lamellaSet.destroy());
        this.lamellaSetObjects = [];
    }

    public async rebuildSets(updatedLamellaSets: any[]): Promise<void> {
        this.data.lamellaSets = updatedLamellaSets; // Päivitetään data
        this.removeLamellaSets();
        return this.insertLamellaSets(updatedLamellaSets).then(()=>this._changed.next());
    }

    /**
     * Palauttaa asennusprofiilien sijainnit annetun orientaation mukaan.
     * Huomaa että rakennus suunta eli position nousu on aina x-akseli. Tämä koko ryhmä käännetään myöhemmin.
     * TOISTAISEKSI kova koodattu.
     * @param orientation
     * @private
     */
    private installationProfilePositions(orientation: 'horizontal' | 'vertical'): Vector3[] {

        //dirty fix
        this.data.positions ??= this.data.framePositions;

        if(this.data.positions){
            return this.data.positions.map((position: any) => {
                return new Vector3(position.position / 1000, 0, 0);
            });
        }
        //For testing purposes. this.data should have positions.
        if (orientation === 'horizontal') {
            return [
                new Vector3(0.0, 0.0, 0.0),
                new Vector3(2, 0.0, 0),
            ];
        } else {
            return [
                new Vector3(0.0, 0.0, 0.0),
                new Vector3(1.5, 0, 0),
                new Vector3(3, 0, 0),
            ];
        }
    }

    private calculateLamellaStartingHeight(): number {
        const firstAvailableSlot = 13; // mm, Kammassa käytetty vakio
        const firstLamellaSetSpace = this.data.lamellaSets[0].lamellaSpace;
        return (firstAvailableSlot+firstLamellaSetSpace) / 1000;
    }

    private async insertFrameProfiles(positions: Vector3[], profileData : any) {
        const profileDepth = this.getProfileDepthPosition(profileData);
        const profileHalfWidth = this.getProfileHalfWidth(profileData);
        const assemblyProfileHalfWidth = 0.0205; // FIXME: Kovakoodatttu ja duplicate arvo, refaktoroi

        // Luodaan lupauksia jokaiselle frameProfileObjectin luomiselle
        const frameProfilePromises = positions.map(async (position, index) => {
            // Kopioidaan alkuperäinen sijainti, jotta emme muuta sitä
            const correctedPosition = position.clone();

            // Sijoituskorjaukset ensimmäiselle, viimeiselle ja muille indekseille
            if (index === 0 && correctedPosition.x < assemblyProfileHalfWidth) {
                correctedPosition.x = profileHalfWidth
            } else if (index === positions.length - 1 && correctedPosition.x > (this.data.width/1000) - assemblyProfileHalfWidth) {
                correctedPosition.x = (this.data.width/1000) - profileHalfWidth;
            }

            correctedPosition.z = profileDepth - this.assemblyProfileSlotDepth;

            // Luodaan frameProfileObject ja asetetaan sen sijainti
            const frameProfileObject = await this.object3DFactoryService.createFrameProfileObject({...profileData, length: this.data.height});
            frameProfileObject.position.copy(correctedPosition);
            return frameProfileObject;
        });

        try {

            return Promise.all(frameProfilePromises).then((frameProfileObjects) => {
                frameProfileObjects.forEach(obj => {
                    this.frameProfileObjects.push(obj);
                    this.extend(obj.object());
                });
            }).then(()=>this._changed.next());

        } catch (error) {
            console.error("Virhe frame profile -objektien luomisessa:", error);
        }
    }

    // TODO Laitetaan tulemaan kannasta kun tulee lisää tai lasketaan mallista
    private getProfileDepthPosition(profileData: any) : number {
        if(profileData.object3dUrl == 'p-17839.obj')
            return -0.048;

        if(profileData.object3dUrl == 'p-17061.obj')
            return -0.056;

        return 0;

    }

    // TODO Laitetaan tulemaan kannasta kun tulee lisää tai lasketaan mallista
    private getProfileHalfWidth(profileData: any) : number {
        if(profileData.object3dUrl == 'p-17839.obj')
            return 0.020;

        if(profileData.object3dUrl == 'p-17061.obj')
            return 0.020;

        return 0;

    }

    updateMountingAndFrameProfileMaterialAndColor(mountingProfile: MountingProfile) {
        this.data.mountingProfile = mountingProfile;
        const materialTypeEnum = mountingProfile.materialType === "Powder Coated" ? CustomMaterialType.PowderCoated : CustomMaterialType.AnodizedAluminum;
        this.mountingProfileObjects.forEach((mountingProfileObject) => mountingProfileObject.applyMaterialAndColor(materialTypeEnum, mountingProfile.color));
        this.frameProfileObjects.forEach((frameProfileObject) => frameProfileObject.applyMaterialAndColor(materialTypeEnum, mountingProfile.color));
    }

    updateMountingAndFrameProfileColor(color: ColorInfo) {
        this.mountingProfileObjects.forEach((mountingProfileObject) => mountingProfileObject.setColor(color));
        this.frameProfileObjects.forEach((frameProfile) => frameProfile.setColor(color))
    }

    /**
     * lamellaLength is physical length of the product and lamellaAreaHeight? is the size of the area we want to cover.
     * These will not change if area is rotated.
     * @param width
     * @param height
     */
    async setSize(width: number, height: number): Promise<void> {

       const lamellaLength = this.data.orientation === 'horizontal' ? width : height;
        const lamellaAreaHeight = this.data.orientation === 'horizontal' ? height : width;

        this.data.width = lamellaLength;

        if(lamellaAreaHeight){

            this.data.height = lamellaAreaHeight;

            return Promise.all([

                this.rebuildSets(this.data.lamellaSets),
                this.rebuildMountingProfiles(this.data.mountingProfile, this.data.positions)

            ]).then(()=>{
                this._object.updateMatrixWorld(true);

            })

        } else {
            this.lamellaSetObjects.forEach((lamellaSetObject) => {
                lamellaSetObject.setLength((lamellaLength/1000)*this.scaleFactor);
            });
            this.lamellaSetObjectContainer.position.copy(this.computeLamellaSetContainerPosition());
            this._object.updateMatrixWorld(true);
            return Promise.resolve()
        }
    }

    private computeLamellaSetContainerPosition(): Vector3 {
        // Määritellään lamella-alueen pituus orientaation perusteella.
        // Voit säätää tätä logiikkaa tarpeen mukaan.
        const lamellaLength = this.data.width / 1000 //Always use width at this point!
        // Käytetään laskettua aloituskorkeutta
        const y = this.calculateLamellaStartingHeight();
        // Voit asettaa z-akselin arvon, esim. assemblyProfileSlotDepth tai 0, tarpeen mukaan.
        const z = 0;
        return new Vector3(lamellaLength, y, z);
    }
}
